import type { ReactElement } from 'react';
import React, { useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import type { Transition } from 'd3';
import { select } from 'd3';
import DiagonalHatchPattern from './diagonal-hatch-pattern';
import type { BoxWhiskerData, FillType } from './types';

export default function Box({
  data,
  scaleY,
  boxScaleX,
  bandwidth,
  animationDuration,
  fillType
}: {
  data: BoxWhiskerData;
  scaleY: d3.ScaleLinear<number, number, never>;
  boxScaleX: number;
  bandwidth: number;
  animationDuration: number;
  fillType: FillType;
}): ReactElement {
  /* Only animate on first render */
  const animDuration = useRef(animationDuration);
  useLayoutEffect(() => {
    animDuration.current = 0;
  }, []);

  return (
    <>
      {fillType === 'diagonal-hatch' && <DiagonalHatchPattern />}
      <g className={clsx('box', data.className, fillType)}>
        {/* invisible background */}
        <rect
          opacity={0}
          x={`${boxScaleX}`}
          width={`${bandwidth}`}
          y={scaleY(data.maximum)}
          height={scaleY(data.minimum) - scaleY(data.maximum)}
        />
        {/* upper box */}
        <rect
          x={`${boxScaleX}`}
          width={`${bandwidth}`}
          fillOpacity="0.6"
          fill="white"
          stroke="black"
          strokeWidth="2px"
          ref={(node): Transition<SVGRectElement | null, unknown, null, undefined> =>
            select(node)
              .attr('height', 0)
              .attr('y', `${scaleY(data.median)}`)
              .transition()
              .delay(0 * animDuration.current)
              .duration(animDuration.current)
              .attr('y', `${scaleY(data.upperQuartile)}`)
              .attr('height', `${scaleY(data.median) - scaleY(data.upperQuartile)}`)
          }
        />
        {/* lower box */}
        <rect
          x={`${boxScaleX}`}
          y={`${scaleY(data.median)}`}
          width={`${bandwidth}`}
          fillOpacity="0.6"
          fill="white"
          stroke="black"
          strokeWidth="2px"
          ref={(node): Transition<SVGRectElement | null, unknown, null, undefined> =>
            select(node)
              .attr('height', 0)
              .transition()
              .delay(0 * animDuration.current)
              .duration(animDuration.current)
              .attr('height', `${scaleY(data.lowerQuartile) - scaleY(data.median)}`)
          }
        />
      </g>
      {/* whisker  */}
      <g
        className={clsx('whisker', data.className)}
        ref={(node): void => {
          select(node)
            .attr('opacity', 0)
            .transition()
            .delay(1 * animDuration.current)
            .duration(animDuration.current)
            .attr('opacity', 1);
        }}
      >
        {/* upper whisker */}
        <line
          x1={`${boxScaleX + bandwidth / 2}`}
          x2={`${boxScaleX + bandwidth / 2}`}
          y1={`${scaleY(data.upperQuartile)}`}
          y2={`${scaleY(data.maximum)}`}
          stroke="black"
          strokeWidth="1px"
          strokeDasharray="3,3"
        />
        <line
          x1={`${boxScaleX}`}
          x2={`${boxScaleX + bandwidth}`}
          y1={`${scaleY(data.maximum)}`}
          y2={`${scaleY(data.maximum)}`}
          stroke="black"
          strokeWidth="2px"
        />
        {/* lower whisker */}
        <line
          x1={`${boxScaleX + bandwidth / 2}`}
          x2={`${boxScaleX + bandwidth / 2}`}
          y1={`${scaleY(data.lowerQuartile)}`}
          y2={`${scaleY(data.minimum)}`}
          stroke="black"
          strokeWidth="1px"
          strokeDasharray="3,3"
        />
        <line
          x1={`${boxScaleX}`}
          x2={`${boxScaleX + bandwidth}`}
          y1={`${scaleY(data.minimum)}`}
          y2={`${scaleY(data.minimum)}`}
          stroke="black"
          strokeWidth="2px"
        />
      </g>
    </>
  );
}
